import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Code, Search, BarChart, Globe, FileText, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

export default function SiteScripts() {
  const [scripts, setScripts] = useState({
    head_scripts: '',
    body_scripts: '',
    google_analytics_id: '',
    google_search_console_code: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [sitemapStatus, setSitemapStatus] = useState({ last_generated: null });
  const [robotsSettings, setRobotsSettings] = useState({ content: '', last_generated: null });
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    fetchScripts();
    fetchSitemapStatus();
    fetchRobotsSettings();
  }, []);

  const fetchScripts = async () => {
    try {
      const response = await apiClient.get('/admin/site-scripts');
      setScripts(response.data);
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  const fetchSitemapStatus = async () => {
    try {
      const res = await apiClient.get('/admin/sitemap-status');
      setSitemapStatus(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchRobotsSettings = async () => {
    try {
      const res = await apiClient.get('/admin/robots-settings');
      setRobotsSettings(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      await apiClient.put('/admin/site-scripts', scripts);
      toast.success('Site scripts updated successfully! Please refresh the website to see changes.');
    } catch (error) {
      toast.error('Failed to update site scripts');
    } finally {
      setIsSaving(false);
    }
  };

  const generateGAScript = () => {
    if (!scripts.google_analytics_id) {
      toast.error('Please enter Google Analytics ID first');
      return;
    }
    
    const gaScript = `<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${scripts.google_analytics_id}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${scripts.google_analytics_id}');
</script>`;
    
    setScripts({ ...scripts, head_scripts: scripts.head_scripts + '\n' + gaScript });
    toast.success('Google Analytics code added to Head Scripts');
  };

  const generateGSCScript = () => {
    if (!scripts.google_search_console_code) {
      toast.error('Please enter Google Search Console verification code first');
      return;
    }
    
    const gscScript = `<meta name="google-site-verification" content="${scripts.google_search_console_code}" />`;
    
    setScripts({ ...scripts, head_scripts: scripts.head_scripts + '\n' + gscScript });
    toast.success('Google Search Console meta tag added to Head Scripts');
  };

  const handleGenerateSitemap = async () => {
    setIsGenerating(true);
    try {
      const res = await apiClient.post('/admin/generate-sitemap');
      toast.success(res.data.message);
      fetchSitemapStatus();
    } catch (e) {
      toast.error('Failed to generate sitemap');
    } finally { setIsGenerating(false); }
  };

  const handleGenerateRobots = async () => {
    try {
      await apiClient.put('/admin/robots-settings', { content: robotsSettings.content });
      const res = await apiClient.post('/admin/generate-robots');
      toast.success(res.data.message);
      fetchRobotsSettings();
    } catch (e) {
      toast.error('Failed to generate robots.txt');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
              <Code className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Site Scripts</h1>
              <p className="text-gray-600">Manage Google Analytics, Search Console, and custom scripts</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            
            {/* Quick Setup Section */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Setup</h2>
              <p className="text-sm text-gray-600 mb-4">Enter your IDs below, then click the generate buttons to auto-create the scripts</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Google Analytics */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <BarChart size={20} className="text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Google Analytics</h3>
                  </div>
                  <Label className="text-sm">Measurement ID</Label>
                  <Input
                    value={scripts.google_analytics_id}
                    onChange={(e) => setScripts({ ...scripts, google_analytics_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX or UA-XXXXXXXXX-X"
                    className="mb-3"
                  />
                  <Button
                    type="button"
                    onClick={generateGAScript}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    Generate GA Script
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Find this in: Google Analytics → Admin → Data Streams → Measurement ID
                  </p>
                </div>

                {/* Google Search Console */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Search size={20} className="text-green-600" />
                    <h3 className="font-semibold text-gray-900">Google Search Console</h3>
                  </div>
                  <Label className="text-sm">Verification Code</Label>
                  <Input
                    value={scripts.google_search_console_code}
                    onChange={(e) => setScripts({ ...scripts, google_search_console_code: e.target.value })}
                    placeholder="abcdefghijklmnopqrstuvwxyz123456"
                    className="mb-3"
                  />
                  <Button
                    type="button"
                    onClick={generateGSCScript}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    Generate Meta Tag
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Find this in: Search Console → Settings → Verification → HTML tag method
                  </p>
                </div>
              </div>
            </div>

            {/* Sitemap & Robots.txt Section */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">SEO Files</h2>
              <p className="text-sm text-gray-600 mb-4">Manage your sitemap.xml and robots.txt for search engine optimization</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sitemap */}
                <div className="border rounded-lg p-4" data-testid="sitemap-section">
                  <div className="flex items-center space-x-2 mb-3">
                    <Globe size={20} className="text-purple-600" />
                    <h3 className="font-semibold text-gray-900">XML Sitemap</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Auto-generates from all public pages, spaces, blog posts, and gallery locations.</p>
                  <div className="bg-gray-50 rounded p-2 mb-3">
                    <code className="text-xs text-gray-700 break-all">https://thecircuit.work/sitemap.xml</code>
                  </div>
                  {sitemapStatus.last_generated && (
                    <p className="text-xs text-green-600 mb-3">Last generated: {new Date(sitemapStatus.last_generated).toLocaleString()}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={handleGenerateSitemap}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      disabled={isGenerating}
                      data-testid="generate-sitemap-btn"
                    >
                      <RefreshCw size={14} className={`mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                      {isGenerating ? 'Generating...' : 'Generate Sitemap'}
                    </Button>
                    <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer">
                      <Button type="button" variant="outline" size="sm" data-testid="view-sitemap-btn">
                        <ExternalLink size={14} className="mr-1" /> View
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Robots.txt */}
                <div className="border rounded-lg p-4" data-testid="robots-section">
                  <div className="flex items-center space-x-2 mb-3">
                    <FileText size={20} className="text-orange-600" />
                    <h3 className="font-semibold text-gray-900">Robots.txt</h3>
                  </div>
                  <div className="bg-gray-50 rounded p-2 mb-3">
                    <code className="text-xs text-gray-700 break-all">https://thecircuit.work/robots.txt</code>
                  </div>
                  <Textarea
                    value={robotsSettings.content}
                    onChange={(e) => setRobotsSettings({ ...robotsSettings, content: e.target.value })}
                    rows={5}
                    className="font-mono text-xs mb-3"
                    data-testid="robots-content"
                  />
                  {robotsSettings.last_generated && (
                    <p className="text-xs text-green-600 mb-3">Last generated: {new Date(robotsSettings.last_generated).toLocaleString()}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={handleGenerateRobots}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      data-testid="generate-robots-btn"
                    >
                      <RefreshCw size={14} className="mr-1" /> Save & Generate
                    </Button>
                    <a href="/api/robots.txt" target="_blank" rel="noopener noreferrer">
                      <Button type="button" variant="outline" size="sm" data-testid="view-robots-btn">
                        <ExternalLink size={14} className="mr-1" /> View
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Head Scripts */}
            <div>
              <Label className="text-lg font-semibold">Head Scripts</Label>
              <p className="text-sm text-gray-600 mb-3">
                Scripts added here will be inserted in the &lt;head&gt; section of your website.
                <br />
                <span className="text-blue-600">Use for:</span> Google Analytics, Meta tags, SEO tags, CSS files
              </p>
              <Textarea
                value={scripts.head_scripts}
                onChange={(e) => setScripts({ ...scripts, head_scripts: e.target.value })}
                placeholder={`<!-- Example: Google Analytics -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXXXXXX');\n</script>\n\n<!-- Example: Google Search Console Verification -->\n<meta name="google-site-verification" content="your-verification-code" />`}
                rows={12}
                className="font-mono text-sm"
              />
            </div>

            {/* Body Scripts */}
            <div>
              <Label className="text-lg font-semibold">Body Scripts</Label>
              <p className="text-sm text-gray-600 mb-3">
                Scripts added here will be inserted at the end of the &lt;body&gt; section.
                <br />
                <span className="text-blue-600">Use for:</span> Chat widgets, Pop-up tools, Performance monitoring
              </p>
              <Textarea
                value={scripts.body_scripts}
                onChange={(e) => setScripts({ ...scripts, body_scripts: e.target.value })}
                placeholder={`<!-- Example: Facebook Pixel -->\n<script>\n  !function(f,b,e,v,n,t,s)\n  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?\n  n.callMethod.apply(n,arguments):n.queue.push(arguments)};\n  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\n  n.queue=[];t=b.createElement(e);t.async=!0;\n  t.src=v;s=b.getElementsByTagName(e)[0];\n  s.parentNode.insertBefore(t,s)}(window, document,'script',\n  'https://connect.facebook.net/en_US/fbevents.js');\n  fbq('init', 'YOUR_PIXEL_ID');\n  fbq('track', 'PageView');\n</script>`}
                rows={12}
                className="font-mono text-sm"
              />
            </div>

            {/* Important Notes */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes:</h3>
              <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                <li>Always test your scripts before saving to production</li>
                <li>Invalid scripts may break your website - double check syntax</li>
                <li>Changes take effect immediately after saving and refreshing the website</li>
                <li>Keep a backup of your scripts before making major changes</li>
                <li>Remove any unused scripts to improve page load speed</li>
              </ul>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button type="submit" disabled={isSaving}>
                <Save size={18} className="mr-2" />
                {isSaving ? 'Saving...' : 'Save Scripts'}
              </Button>
            </div>
          </div>
        </form>

        {/* Help Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-3">📚 Common Integrations:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <strong>Google Analytics 4 (GA4):</strong>
              <p>Track website visitors, page views, and user behavior</p>
            </div>
            <div>
              <strong>Google Search Console:</strong>
              <p>Verify ownership and monitor search performance</p>
            </div>
            <div>
              <strong>Facebook Pixel:</strong>
              <p>Track conversions and build audiences for ads</p>
            </div>
            <div>
              <strong>Google Tag Manager:</strong>
              <p>Manage all your marketing tags without code changes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
